import React from 'react';
import { Video, Tag, Eye, Layers, Image } from 'lucide-react';
import { YouTubeContent } from '../types/socialMedia.types';
import CharacterCounter from '../components/CharacterCounter';
import ScheduleSettings from '../components/ScheduleSettings';
import { PLATFORMS } from '../config/platformConfig';

interface YouTubeEditorProps {
  content: YouTubeContent;
  isEditing: boolean;
  onChange: (field: keyof YouTubeContent, value: any) => void;
}

const YOUTUBE_CATEGORIES = [
  'Education', 'Entertainment', 'Gaming', 'Howto & Style',
  'Music', 'News & Politics', 'Nonprofits & Activism',
  'People & Blogs', 'Pets & Animals', 'Science & Technology',
  'Sports', 'Travel & Events', 'Autos & Vehicles',
  'Comedy', 'Film & Animation'
];

export default function YouTubeEditor({ content, isEditing, onChange }: YouTubeEditorProps) {
  const platform = PLATFORMS.youtube;
  const tagsCharCount = content.tags.length;

  return (
    <div className="space-y-4">
      {/* Content Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Video className="inline w-4 h-4 mr-1" />
          Content Type
        </label>
        <select
          value={content.postType}
          onChange={(e) => onChange('postType', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
        >
          {platform.postTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title {content.postType === 'Shorts' && '(Shorts)'}
        </label>
        <input
          type="text"
          value={content.title}
          onChange={(e) => onChange('title', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
          placeholder={content.postType === 'Shorts' ? 'Short, catchy title...' : 'Video title...'}
          maxLength={100}
        />
        <CharacterCounter
          current={content.title.length}
          limit={100}
          label="Title"
          showProgressBar={true}
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={content.description}
          onChange={(e) => onChange('description', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100 h-32"
          placeholder="Tell viewers about your video..."
          maxLength={5000}
        />
        <CharacterCounter
          current={content.description.length}
          limit={5000}
          label="Description"
        />
        <p className="text-xs text-gray-500 mt-1">
          First 125 characters appear in search results
        </p>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Tag className="inline w-4 h-4 mr-1" />
          Tags
        </label>
        <textarea
          value={content.tags}
          onChange={(e) => onChange('tags', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100 h-16"
          placeholder="tag1, tag2, tag3 (comma-separated)"
        />
        <CharacterCounter
          current={tagsCharCount}
          limit={500}
          label="Total tag characters"
          showProgressBar={false}
        />
        <p className="text-xs text-gray-500 mt-1">
          Tags help viewers find your content
        </p>
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Layers className="inline w-4 h-4 mr-1" />
          Category
        </label>
        <select
          value={content.category}
          onChange={(e) => onChange('category', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
        >
          <option value="">Select category...</option>
          {YOUTUBE_CATEGORIES.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Visibility */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Eye className="inline w-4 h-4 mr-1" />
          Visibility
        </label>
        <select
          value={content.visibility}
          onChange={(e) => onChange('visibility', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
        >
          <option value="Public">Public</option>
          <option value="Unlisted">Unlisted</option>
          <option value="Private">Private</option>
          {content.postType === 'Premiere' && (
            <option value="Scheduled">Scheduled Premiere</option>
          )}
        </select>
      </div>

      {/* Thumbnail URL */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Image className="inline w-4 h-4 mr-1" />
          Thumbnail URL
        </label>
        <input
          type="url"
          value={content.thumbnail}
          onChange={(e) => onChange('thumbnail', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
          placeholder="https://example.com/thumbnail.jpg"
        />
        {content.thumbnail && (
          <div className="mt-2">
            <img
              src={content.thumbnail}
              alt="Thumbnail preview"
              className="h-20 rounded border"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}
      </div>

      {/* Schedule */}
      <ScheduleSettings
        scheduledDate={content.scheduledDate}
        scheduledTime={content.scheduledTime}
        onDateChange={(date) => onChange('scheduledDate', date)}
        onTimeChange={(time) => onChange('scheduledTime', time)}
        disabled={!isEditing}
        platformName="YouTube"
      />
    </div>
  );
}